import { ArrayInput, NumberInput, SimpleFormIterator, TextInput } from "@/components/admin";
import { HSNFieldWrapper, HSNSuggestionsWrapper } from "./HSNAutocomplete";
import { InventoryItemPickerWrapper } from "./InventoryItemPicker";
import { calculateTax } from "./TaxCalculator";
import { UQCAutocomplete } from "./UQCAutocomplete";

import { useSimpleFormIterator, useSimpleFormIteratorItem } from "ra-core";
import { memo, useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";

// ── ItemCalculations ─────────────────────────────────────────────────────────
const ItemCalculations = memo(({ source }: { source: string }) => {
  const { setValue } = useFormContext();

  const quantity = useWatch({ name: `${source}.quantity` }) || 0;
  const unitPrice = useWatch({ name: `${source}.unit_price` }) || 0;
  const taxRate = useWatch({ name: `${source}.tax_rate` }) || 0;

  const customerState = useWatch({ name: "customer_state" }) || "MH";
  const companyState = "MH";

  const amount = quantity * unitPrice;
  const taxResult = calculateTax({ amount, taxRate, customerState, companyState });
  const lineTotal = amount + taxResult.totalTax;

  useEffect(() => {
    setValue(`${source}.tax_amount`, taxResult.totalTax, { shouldDirty: true });
    setValue(`${source}.line_total`, lineTotal, { shouldDirty: true });
  }, [taxResult.totalTax, lineTotal, source, setValue]);

  return (
    <div className="flex flex-col items-end justify-center w-full h-[2.75rem] px-3 bg-[#e8f7f5] rounded-lg border border-[#a3e2d7] shadow-xs">
      <span className="font-bold text-sm text-[#007e6b]">
        ₹{lineTotal.toFixed(2)}
      </span>
      <span className="text-[10px] text-[#00947e] font-bold leading-none mt-0.5">
        Includes ₹{taxResult.totalTax.toFixed(2)}
      </span>
    </div>
  );
});
ItemCalculations.displayName = 'ItemCalculations';

// ── MarginDisplay (two-tier quotation) ──────────────────────────────────────
// Shows margin % = (price - cost) / cost. Internal only — never on the customer PDF.
const MarginDisplay = ({ source }: { source: string }) => {
  const cost = Number(useWatch({ name: `${source}.cost_price` })) || 0;
  const price = Number(useWatch({ name: `${source}.unit_price` })) || 0;
  const margin = cost > 0 ? ((price - cost) / cost) * 100 : 0;
  const color = margin < 0 ? "#dc2626" : margin === 0 ? "#64748b" : "#007e6b";
  return (
    <div className="flex items-center h-[2.75rem] px-2 text-xs font-bold" style={{ color }}>
      {cost > 0 ? `${margin.toFixed(1)}%` : "—"}
    </div>
  );
};

const MarginDisplayWrapper = () => {
  const item = useSimpleFormIteratorItem();
  const iterator = useSimpleFormIterator();
  if (!item || !iterator) return null;
  return <MarginDisplay source={`${iterator.source}.${item.index}`} />;
};

// ── UnitField ────────────────────────────────────────────────────────────────
const UnitField = ({ arraySource, index }: { arraySource: string; index: number }) => {
  const unit = useWatch({ name: `${arraySource}.${index}.unit` });
  return (
    <UQCAutocomplete
      key={unit ?? "__empty__"}
      source="unit"
      label={false}
      placeholder="Select UQC"
      className="w-full"
    />
  );
};

// ── UnitFieldWrapper ─────────────────────────────────────────────────────────
const UnitFieldWrapperInside = () => {
  const item = useSimpleFormIteratorItem();
  const iterator = useSimpleFormIterator();
  if (!item || !iterator) return null;
  return <UnitField arraySource={iterator.source} index={item.index} />;
};

const UnitFieldWrapper = () => {
  try {
    return <UnitFieldWrapperInside />;
  } catch {
    return null;
  }
};

// ── ItemCalculationsWrapper ──────────────────────────────────────────────────
const ItemCalculationsWrapperInside = () => {
  const item = useSimpleFormIteratorItem();
  const iterator = useSimpleFormIterator();
  if (!item || !iterator) return null;
  const itemSource = `${iterator.source}.${item.index}`;
  return <ItemCalculations source={itemSource} />;
};

const ItemCalculationsWrapper = () => {
  try {
    return <ItemCalculationsWrapperInside />;
  } catch {
    return null;
  }
};

// ── DocumentItemsTable ───────────────────────────────────────────────────────
export const DocumentItemsTable = ({
  source = "items",
  showPercentage = false,
  showCost = false,
  showSubItems = false,
}: {
  source?: string;
  showPercentage?: boolean;
  /** Two-tier quotation: show an internal Cost column + Margin % (never printed). */
  showCost?: boolean;
  /** TC-04: per-item numbered sub-detail lines under the HSN/description heading. */
  showSubItems?: boolean;
}) => (
  <div className="w-full space-y-4 bg-transparent compact-table-inputs">
    <div className="w-full">
      <div className="w-full">
        {/* Industry Standard Header Row - hidden in favor of direct card field labels for perfect clarity */}
        <div className="hidden" />

        <ArrayInput source={source} label={false}>
          <SimpleFormIterator
            className="flex flex-col gap-4 pb-28"
            disableReordering
            getItemLabel={() => ""}
          >
            {/* Transparent, border-free item row layout directly on page background */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-x-5 gap-y-3 pb-6 mb-6 border-b border-slate-300 transition-all duration-200 items-start relative group w-full">

              {/* Inventory Picker — optional quick-fill from product catalog */}
              <div className="col-span-12 mb-1">
                <InventoryItemPickerWrapper priceField="unit_price" setTaxRate={true} />
              </div>

              {/* Description & Suggestions */}
              <div className={`${showPercentage ? "col-span-12 md:col-span-6" : "col-span-12 md:col-span-6"} flex flex-col relative w-full`}>
                <div className="text-xs font-bold text-slate-500 mb-1.5">Item Description</div>
                <TextInput
                  source="description"
                  label={false}
                  placeholder="Enter item description..."
                  helperText={false}
                  className="w-full !mt-0"
                />
                <HSNSuggestionsWrapper />
              </div>

              {/* HSN */}
              <div className="col-span-12 md:col-span-3 w-full">
                <div className="text-xs font-bold text-slate-500 mb-1.5">HSN / SAC</div>
                <HSNFieldWrapper label={false} className="w-full !mt-0" />
              </div>

              {/* Unit (UQC) */}
              <div className="col-span-12 md:col-span-3 w-full">
                <div className="text-xs font-bold text-slate-500 mb-1.5">UQC (Unit)</div>
                <UnitFieldWrapper />
              </div>

              {/* Quantity */}
              <div className="col-span-12 md:col-span-2 w-full">
                <div className="text-xs font-bold text-slate-500 mb-1.5">Qty</div>
                <NumberInput
                  source="quantity"
                  label={false}
                  placeholder="0.00"
                  helperText={false}
                  className="w-full !mt-0"
                />
              </div>

              {/* Cost Price (two-tier quotation — internal, Operations) */}
              {showCost && (
                <div className="col-span-12 md:col-span-2 w-full">
                  <div className="text-xs font-bold text-amber-600 mb-1.5">Cost (₹)</div>
                  <NumberInput
                    source="cost_price"
                    label={false}
                    placeholder="0.00"
                    helperText={false}
                    className="w-full !mt-0"
                  />
                </div>
              )}

              {/* Unit Price */}
              <div className="col-span-12 md:col-span-3 w-full">
                <div className="text-xs font-bold text-slate-500 mb-1.5">{showCost ? "Customer Price (₹)" : "Price (₹)"}</div>
                <NumberInput
                  source="unit_price"
                  label={false}
                  placeholder="0.00"
                  helperText={false}
                  className="w-full !mt-0"
                />
              </div>

              {/* Margin % (two-tier quotation — internal) */}
              {showCost && (
                <div className="col-span-12 md:col-span-1 w-full">
                  <div className="text-xs font-bold text-slate-500 mb-1.5">Margin</div>
                  <MarginDisplayWrapper />
                </div>
              )}

              {/* GST % */}
              <div className="col-span-12 md:col-span-2 w-full">
                <div className="text-xs font-bold text-slate-500 mb-1.5">GST %</div>
                <NumberInput
                  source="tax_rate"
                  label={false}
                  placeholder="0"
                  helperText={false}
                  className="w-full !mt-0"
                />
              </div>

              {/* % Applied */}
              {showPercentage && (
                <div className="col-span-12 md:col-span-2 w-full">
                  <div className="text-xs font-bold text-slate-500 mb-1.5">% Applied</div>
                  <NumberInput
                    source="percentage"
                    label={false}
                    placeholder="100%"
                    helperText={false}
                    className="w-full !mt-0"
                  />
                </div>
              )}

              {/* Row Total Calculator */}
              <div className={`col-span-12 ${showPercentage ? "md:col-span-3" : "md:col-span-3"} flex flex-col justify-start w-full`}>
                <div className="text-xs font-bold text-slate-500 mb-1.5">Total (₹)</div>
                <ItemCalculationsWrapper />
              </div>

              {/* TC-04: Sub-detail lines under the HSN heading (optional, numbered) */}
              {showSubItems && (
                <div className="col-span-12 ml-1 border-l-2 border-slate-200 pl-4">
                  <div className="text-xs font-bold text-slate-500 mb-2">
                    Sub-items (optional — numbered detail lines under this HSN heading)
                  </div>
                  <ArrayInput source="sub_items" label={false}>
                    <SimpleFormIterator className="flex flex-col gap-2" disableReordering>
                      <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start w-full">
                        <div className="col-span-12 md:col-span-9">
                          <TextInput
                            source="text"
                            label={false}
                            placeholder="Sub-item description (e.g. Chain link 60kg)"
                            helperText={false}
                            className="w-full !mt-0"
                          />
                        </div>
                        <div className="col-span-12 md:col-span-3">
                          <TextInput
                            source="qty_note"
                            label={false}
                            placeholder="Total (e.g. 400)"
                            helperText={false}
                            className="w-full !mt-0"
                          />
                        </div>
                      </div>
                    </SimpleFormIterator>
                  </ArrayInput>
                </div>
              )}

            </div>
          </SimpleFormIterator>
        </ArrayInput>
      </div>
    </div>
  </div>
);